/**
 * 共用 settlement 列表 panel(REQ-20260522 Phase 3B-Lite):
 * Customer AR / Supplier AP 共用 UI。差別只在:
 *   - 標題(應收 / 應付)
 *   - 開帳欄位 label(期初應收 vs 期初應付)
 *   - 「沖帳當下餘額」欄位(receivableAfter / payableAfter)
 *   - line detail fetch path
 *
 * Component 接 generic data shape,呼叫端傳 hook 結果進來。
 */
import { Fragment, useState } from 'react';
import { formatMoney } from '../../lib/format-money';
import { useApSettlement, useArSettlement, SETTLEMENTS_PAGE_SIZE } from './api';

export type SettlementSide = 'ar' | 'ap';

interface RowItem {
  id: number;
  documentNo: string;
  subCode: number;
  settledAt: string;
  legacyPartyCode: string | null;
  cashAmount: string | null;
  checkAmount: string | null;
  usePrepaid: string | null;
  totalSettled: string;
  /** AR: receivableAfter / AP: payableAfter */
  balanceAfter: string | null;
  prepaidAfter: string | null;
  source: string;
  lineCount: number;
  /** 以下三個只有應收側有(REQ-20260817006),AP 呼叫端不傳 */
  checkClearDate?: string | null;
  transferAmount?: string | null;
  transferAccountLast4?: string | null;
  /**
   * 解除沖銷用的兩個狀態(應收 REQ-20260821001;應付 REQ-20261029002 起也有)。
   * `reversalOfId` 有值 = 這一列本身是解除沖銷單;
   * `reversedByDocumentNo` 有值 = 這筆收付款已經被那張解除沖銷單抵銷掉了。
   */
  reversalOfId?: number | null;
  reversedByDocumentNo?: string | null;
}

interface Props {
  side: SettlementSide;
  loading: boolean;
  error: string | null;
  /** list response 的 items 標準化 + balance 欄位名取代後傳進來 */
  rows: RowItem[];
  total: number;
  opening: { receivableOrPayable: string | null; prepaid: string | null };
  totalSettledSum: string;
  page: number;
  onPageChange: (page: number) => void;
  /**
   * 要不要顯示「支票兌現日」與「匯款」兩欄(REQ-20260817006)。
   * **未傳就不顯示** —— 這兩個欄位只存在於 `ar_settlements`,應付側的
   * `ap_settlements` 沒有,長出兩排永遠是「—」的欄位只是雜訊。
   * 用 prop 而不是 `side === 'ar'` 判斷:欄位顯示與否綁在「呼叫端有沒有餵資料」上,
   * 而不是綁在標題該寫應收還是應付。
   */
  showTransferAndCheckClear?: boolean;
  /**
   * 按下「⚠️ 解除沖銷」時要做什麼(REQ-20260821001;應付側 REQ-20261029002 起也傳)。
   *
   * **未傳就整欄不出現**:非 ADMIN 不該看到這顆按鈕(後端一樣擋,前端只是不給入口)。
   * 確認 / 填原因由呼叫端處理 —— 這支是兩個 panel 共用的唯讀表格,維持不帶表單狀態。
   */
  onReverse?: (row: RowItem) => void;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('zh-TW');
}

export function SettlementListPanel(props: Props): JSX.Element {
  const { side, loading, error, rows, total, opening, totalSettledSum, page, onPageChange } = props;
  const showTransferAndCheckClear = props.showTransferAndCheckClear ?? false;
  const onReverse = props.onReverse;
  const [expanded, setExpanded] = useState<number | null>(null);

  const pages = Math.max(1, Math.ceil(total / SETTLEMENTS_PAGE_SIZE));
  /** 展開明細那一列要橫跨的欄數,跟著上面的表頭走 */
  const colCount = (showTransferAndCheckClear ? 11 : 9) + (onReverse ? 1 : 0);

  const openingLabel = side === 'ar' ? '期初應收' : '期初應付';
  const prepaidLabel = side === 'ar' ? '期初預收' : '期初預付';
  const balanceColLabel = side === 'ar' ? '應收餘額(沖帳當下)' : '應付餘額(沖帳當下)';

  return (
    <div className="grid gap-3">
      {/* 摘要 */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div className="bg-slate-50 border rounded p-3">
          <div className="text-xs text-slate-500">{openingLabel}</div>
          <div className="text-base font-medium tabular-nums">
            {formatMoney(opening.receivableOrPayable)}
          </div>
        </div>
        <div className="bg-slate-50 border rounded p-3">
          <div className="text-xs text-slate-500">{prepaidLabel}</div>
          <div className="text-base font-medium tabular-nums">{formatMoney(opening.prepaid)}</div>
        </div>
        <div className="bg-slate-50 border rounded p-3">
          <div className="text-xs text-slate-500">
            沖帳累計({total > 0 ? `${total} 筆` : '無記錄'})
          </div>
          <div className="text-base font-medium tabular-nums text-emerald-700">
            {formatMoney(totalSettledSum)}
          </div>
        </div>
      </div>

      {/* 列表 */}
      {error ? (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-slate-500 p-3">載入中…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-500 p-3">尚無沖帳記錄</div>
      ) : (
        <>
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left text-xs text-slate-600">
                  <th className="px-2 py-1.5 w-8"></th>
                  <th className="px-2 py-1.5 w-32">收付單號</th>
                  <th className="px-2 py-1.5 w-28">沖帳日期</th>
                  <th className="px-2 py-1.5 w-24 text-right">現金</th>
                  <th className="px-2 py-1.5 w-24 text-right">票據</th>
                  {showTransferAndCheckClear ? (
                    <>
                      <th className="px-2 py-1.5 w-28">支票兌現日</th>
                      <th className="px-2 py-1.5 w-28 text-right">匯款</th>
                    </>
                  ) : null}
                  <th className="px-2 py-1.5 w-24 text-right">取用預收</th>
                  <th className="px-2 py-1.5 w-24 text-right">沖帳合計</th>
                  <th className="px-2 py-1.5 w-36 text-right">{balanceColLabel}</th>
                  <th className="px-2 py-1.5 w-20 text-right">明細</th>
                  {onReverse ? <th className="px-2 py-1.5 w-24 text-right">解除沖銷</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr
                      className="border-b last:border-b-0 hover:bg-blue-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    >
                      <td className="px-2 py-1.5 text-slate-400">{expanded === r.id ? '▾' : '▸'}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{r.documentNo}</td>
                      <td className="px-2 py-1.5 text-xs">{fmtDate(r.settledAt)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(r.cashAmount)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(r.checkAmount)}
                      </td>
                      {showTransferAndCheckClear ? (
                        <>
                          <td className="px-2 py-1.5 text-xs">{fmtDate(r.checkClearDate)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {formatMoney(r.transferAmount)}
                            {/* 帳號末 4 碼跟著金額走 —— 對帳時要認出錢從哪個帳戶進來,
                                但不值得單獨占一欄 */}
                            {r.transferAccountLast4 ? (
                              <span className="ml-1 text-[10px] text-slate-500 font-mono">
                                {r.transferAccountLast4}
                              </span>
                            ) : null}
                          </td>
                        </>
                      ) : null}
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(r.usePrepaid)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium text-emerald-700">
                        {formatMoney(r.totalSettled)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatMoney(r.balanceAfter)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-xs text-slate-500">
                        {r.lineCount > 0 ? `${r.lineCount} 筆` : '—'}
                      </td>
                      {onReverse ? (
                        <td className="px-2 py-1.5 text-right text-xs">
                          {r.reversalOfId != null ? (
                            <span className="text-slate-400" title="這一列本身就是解除沖銷單">
                              解除單
                            </span>
                          ) : r.reversedByDocumentNo ? (
                            <span
                              className="text-amber-700"
                              title={`已由 ${r.reversedByDocumentNo} 解除沖銷`}
                            >
                              已解除
                            </span>
                          ) : (
                            <button
                              type="button"
                              // 整列點下去是展開明細,這顆不能跟著觸發
                              onClick={(e) => {
                                e.stopPropagation();
                                onReverse(r);
                              }}
                              className="text-red-600 hover:underline"
                              data-testid={`settlement-reverse-btn-${r.id}`}
                            >
                              ⚠️ 解除沖銷
                            </button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                    {expanded === r.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan={colCount} className="px-3 py-2">
                          <LinesInline settlementId={r.id} side={side} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* 分頁 */}
          {pages > 1 ? (
            <div className="flex items-center justify-end gap-2 text-sm">
              <button
                type="button"
                onClick={() => onPageChange(Math.max(0, page - 1))}
                disabled={page === 0}
                className="border rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-30"
              >
                ← 上一頁
              </button>
              <span className="text-slate-600 text-xs">
                第 {page + 1} / {pages} 頁
              </span>
              <button
                type="button"
                onClick={() => onPageChange(Math.min(pages - 1, page + 1))}
                disabled={page + 1 >= pages}
                className="border rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-30"
              >
                下一頁 →
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * 展開行內顯示沖帳明細(該 settlement 的 lines)。獨立 fetch,點開才查。
 */
function LinesInline({
  settlementId,
  side,
}: {
  settlementId: number;
  side: SettlementSide;
}): JSX.Element {
  const arQuery = useArSettlement(side === 'ar' ? settlementId : null);
  const apQuery = useApSettlement(side === 'ap' ? settlementId : null);
  const query = side === 'ar' ? arQuery : apQuery;

  if (query.isLoading) return <div className="text-xs text-slate-500">載入明細…</div>;
  if (query.isError)
    return <div className="text-xs text-red-700">明細載入失敗</div>;
  if (!query.data) return <div className="text-xs text-slate-500">無資料</div>;

  const detail = query.data;
  const lines = detail.lines;
  if (lines.length === 0) {
    return <div className="text-xs text-slate-500">此沖帳單無明細</div>;
  }
  return (
    <div className="grid gap-2">
      <div className="text-xs text-slate-600">
        舊對象代碼:<span className="font-mono">{detail.legacyPartyCode ?? '—'}</span>
        {' · '}建立:{fmtDate(detail.createdAt)}
        {detail.source !== 'manual' ? (
          <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
            {detail.source}
          </span>
        ) : null}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-slate-500">
            <th className="px-2 py-1 w-28">原單據</th>
            <th className="px-2 py-1 w-16">單別</th>
            <th className="px-2 py-1 w-24">單據日期</th>
            <th className="px-2 py-1 w-24 text-right">現行餘額</th>
            <th className="px-2 py-1 w-24 text-right">折讓</th>
            <th className="px-2 py-1 w-24 text-right">沖帳金額</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} className="border-b last:border-b-0">
              <td className="px-2 py-1 font-mono">{l.sourceDocNo ?? '—'}</td>
              <td className="px-2 py-1">{l.sourceDocType ?? '—'}</td>
              <td className="px-2 py-1">{fmtDate(l.sourceDate)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{formatMoney(l.currentBalance)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{formatMoney(l.discountAmount)}</td>
              <td className="px-2 py-1 text-right tabular-nums font-medium">
                {formatMoney(l.settledAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
